import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { proposalsApi, clientsApi, companyProfileApi } from '@/services/api';
import api from '@/services/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  FileText, Plus, Trash2, Sparkles, Check, Pencil, ChevronDown, ChevronUp,
  RefreshCw, Loader2, Building, Package, Brain, Download,
} from 'lucide-react';
import type { Proposal, WorkPackage, ProposalSection, ProposalClient, CompanyProfile, TeamMember } from '@/types';

interface ProposalWizardProps {
  projectId: number;
  projectName: string;
}

const SECTION_LABELS: Record<string, string> = {
  executive_summary: 'Executive Summary',
  problem_understanding: 'Problem Understanding',
  proposed_approach: 'Proposed Approach',
  methodology: 'Methodology',
  team_qualifications: 'Team & Qualifications',
  timeline: 'Timeline & Milestones',
  pricing: 'Pricing',
  terms_and_conditions: 'Terms & Conditions',
};

interface WorkPackageTemplate {
  title: string;
  description: string;
  scope: string;
  pricing_model: string;
  category: string;
}

const WP_TEMPLATES: WorkPackageTemplate[] = [
  {
    category: 'Analyse & Bewertung',
    title: 'Software-Qualitätsanalyse',
    description: 'Umfassende Analyse der bestehenden Softwarearchitektur, Codequalität und technischen Schuld.',
    scope: 'Statische Codeanalyse, Architektur-Review, Identifikation technischer Schuld, Bewertung der Testabdeckung, Dokumentation der Ergebnisse in einem Analysebericht mit priorisierten Handlungsempfehlungen.',
    pricing_model: 'fixed',
  },
  {
    category: 'Analyse & Bewertung',
    title: 'Ist-Analyse & Anforderungserhebung',
    description: 'Aufnahme des aktuellen Zustands, Stakeholder-Interviews und Dokumentation der fachlichen und technischen Anforderungen.',
    scope: 'Stakeholder-Workshops, Prozessaufnahme, Gap-Analyse, Erstellung eines Anforderungskatalogs und Priorisierung der Handlungsfelder.',
    pricing_model: 'fixed',
  },
  {
    category: 'Analyse & Bewertung',
    title: 'Machbarkeitsstudie / Proof of Concept',
    description: 'Technische Machbarkeitsprüfung und prototypische Umsetzung zur Risikominimierung.',
    scope: 'Evaluierung technischer Optionen, Erstellung eines funktionsfähigen Prototyps, Risikobewertung, Aufwandsschätzung für die Gesamtumsetzung.',
    pricing_model: 'fixed',
  },
  {
    category: 'Entwicklung & Implementierung',
    title: 'Technische Neurealisierung / Modernisierung',
    description: 'Neuentwicklung oder Modernisierung einer bestehenden Anwendung auf aktuelle Technologien.',
    scope: 'Architekturdesign, Implementierung der Kernfunktionalitäten, Migration bestehender Daten, automatisierte Tests, Dokumentation der technischen Lösung.',
    pricing_model: 'fixed',
  },
  {
    category: 'Entwicklung & Implementierung',
    title: 'Weiterentwicklung & Feature-Entwicklung',
    description: 'Erweiterung einer bestehenden Plattform um neue Funktionalitäten gemäß Anforderungskatalog.',
    scope: 'Anforderungsspezifikation, Implementierung neuer Features, Integration in bestehende Systemlandschaft, Unit- und Integrationstests, Abnahmeunterstützung.',
    pricing_model: 'time_and_material',
  },
  {
    category: 'Entwicklung & Implementierung',
    title: 'Plattform-Migration / Systemablösung',
    description: 'Migration einer Legacy-Anwendung auf eine moderne Plattform inkl. Datenmigration.',
    scope: 'Migrationskonzept, Mapping bestehender Funktionalitäten, schrittweise Ablösung, Datenmigration und -validierung, Parallelbetrieb und Cutover-Planung.',
    pricing_model: 'fixed',
  },
  {
    category: 'Qualitätssicherung & Testing',
    title: 'Testmanagement & Qualitätssicherung',
    description: 'Aufbau oder Optimierung der Teststrategie und Durchführung systematischer Qualitätssicherung.',
    scope: 'Erstellung der Teststrategie, Definition von Testfällen, Aufbau automatisierter Testpipelines, Durchführung von System- und Regressionstests, Testberichterstattung.',
    pricing_model: 'time_and_material',
  },
  {
    category: 'Qualitätssicherung & Testing',
    title: 'Performance- & Lasttests',
    description: 'Systematische Analyse und Optimierung der Anwendungsperformance unter Last.',
    scope: 'Definition von Performance-KPIs, Erstellung von Lastszenarien, Durchführung von Lasttests, Engpass-Analyse, Optimierungsempfehlungen.',
    pricing_model: 'fixed',
  },
  {
    category: 'Betrieb & Wartung',
    title: 'Wartung & Support (jährlich)',
    description: 'Laufende Wartung, Fehleranalyse und Supportleistungen für den produktiven Betrieb.',
    scope: 'Fehleranalyse und -behebung, Anpassungen an geänderte Schnittstellen, Release- und Deployment-Support, technischer Support via Ticket-System, regelmäßige Statusberichte.',
    pricing_model: 'annual',
  },
  {
    category: 'Betrieb & Wartung',
    title: 'DevOps & CI/CD Optimierung',
    description: 'Einrichtung oder Verbesserung der Build-, Test- und Deployment-Pipelines.',
    scope: 'Analyse der bestehenden CI/CD-Infrastruktur, Optimierung der Build-Zeiten, Automatisierung von Deployments, Monitoring-Integration, Dokumentation der Prozesse.',
    pricing_model: 'fixed',
  },
  {
    category: 'Beratung & Coaching',
    title: 'Technische Projektleitung',
    description: 'Operative technische Leitung und Koordination des Entwicklungsteams.',
    scope: 'Sprint-Planung und -Steuerung, technische Entscheidungen, Risikomanagement, Stakeholder-Kommunikation, Fortschrittsberichterstattung, Qualitätskontrolle.',
    pricing_model: 'time_and_material',
  },
  {
    category: 'Beratung & Coaching',
    title: 'Architektur-Beratung & Review',
    description: 'Bewertung und Beratung zur Softwarearchitektur mit konkreten Verbesserungsvorschlägen.',
    scope: 'Architektur-Assessment, Bewertung von Skalierbarkeit und Wartbarkeit, Technologieberatung, Erstellung eines Zielbilds mit Migrationspfad.',
    pricing_model: 'time_and_material',
  },
];

export function ProposalWizard({ projectId, projectName }: ProposalWizardProps) {
  const { t } = useTranslation(['proposals', 'common', 'useCases']);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [clients, setClients] = useState<ProposalClient[]>([]);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingSection, setGeneratingSection] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('setup');

  // Setup form state
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [proposalType, setProposalType] = useState('angebot');
  const [language, setLanguage] = useState('de');
  const [problemDescription, setProblemDescription] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [contactMemberId, setContactMemberId] = useState<number | null>(null);
  const [signatoryName, setSignatoryName] = useState('');

  // Work package form
  const [wpTitle, setWpTitle] = useState('');
  const [wpDescription, setWpDescription] = useState('');
  const [wpScope, setWpScope] = useState('');
  const [wpPricingModel, setWpPricingModel] = useState('fixed');
  const [wpPrice, setWpPrice] = useState('');
  const [wpDuration, setWpDuration] = useState('');

  // New client form
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientIndustry, setNewClientIndustry] = useState('');
  const [newClientContact, setNewClientContact] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');

  // Work package editing
  const [editingWpId, setEditingWpId] = useState<number | null>(null);
  const [editWp, setEditWp] = useState({ title: '', description: '', scope: '', pricing_model: 'fixed', price_amount: '', duration_days: '' });

  // Section editing
  const [editingSection, setEditingSection] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  const getPricingLabel = (model: string) => t(`proposals.pricingModels.${model}`);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [proposalData, clientData, profileData] = await Promise.all([
        proposalsApi.getByProject(projectId),
        clientsApi.list(),
        companyProfileApi.get(),
      ]);
      setClients(clientData || []);
      setCompanyProfile(profileData);

      if (profileData) {
        const team = await companyProfileApi.listTeam(profileData.id);
        setTeamMembers(team || []);
      }

      if (proposalData) {
        setProposal(proposalData);
        setTitle(proposalData.title);
        setSubtitle(proposalData.subtitle || '');
        setProposalType(proposalData.proposal_type || 'angebot');
        setLanguage(proposalData.language || 'de');
        setProblemDescription(proposalData.problem_description || '');
        setSelectedClientId(proposalData.client_id);
        setContactMemberId(proposalData.contact_member_id);
        setSignatoryName(proposalData.signatory_name || '');
        if (proposalData.sections?.length > 0) setActiveTab('sections');
      } else {
        setTitle(projectName);
      }
    } catch {
      // New proposal
    } finally {
      setLoading(false);
    }
  }, [projectId, projectName]);

  useEffect(() => { loadData(); }, [loadData]);

  const createOrUpdateProposal = async () => {
    try {
      if (proposal) {
        const updated = await proposalsApi.update(proposal.id, {
          title, subtitle, proposal_type: proposalType, language,
          problem_description: problemDescription,
          client_id: selectedClientId, contact_member_id: contactMemberId,
          signatory_name: signatoryName,
        });
        setProposal(updated);
        toast.success(t('proposals.proposalUpdated'));
      } else {
        const created = await proposalsApi.create(projectId, {
          title, subtitle, proposal_type: proposalType, language,
          problem_description: problemDescription,
          client_id: selectedClientId,
          company_id: companyProfile?.id,
          contact_member_id: contactMemberId,
          signatory_name: signatoryName,
          work_packages: [],
        });
        setProposal(created);
        toast.success(t('proposals.proposalCreated'));
        setActiveTab('packages');
      }
    } catch {
      toast.error(t('proposals.saveFailed'));
    }
  };

  const addWorkPackage = async () => {
    if (!proposal || !wpTitle.trim()) return;
    try {
      const wp = await proposalsApi.addWorkPackage(proposal.id, {
        title: wpTitle, description: wpDescription, scope: wpScope,
        pricing_model: wpPricingModel,
        price_amount: wpPrice ? parseFloat(wpPrice) : null,
        price_currency: 'EUR',
        duration_days: wpDuration ? parseInt(wpDuration) : null,
        order: proposal.work_packages.length,
      });
      setProposal({ ...proposal, work_packages: [...proposal.work_packages, wp] });
      setWpTitle(''); setWpDescription(''); setWpScope('');
      setWpPrice(''); setWpDuration('');
      toast.success(t('proposals.wpAdded'));
    } catch {
      toast.error(t('proposals.wpAddFailed'));
    }
  };

  const removeWorkPackage = async (wpId: number) => {
    if (!proposal) return;
    try {
      await proposalsApi.deleteWorkPackage(wpId);
      setProposal({
        ...proposal,
        work_packages: proposal.work_packages.filter(wp => wp.id !== wpId),
      });
      toast.success(t('proposals.wpRemoved'));
    } catch {
      toast.error(t('proposals.wpRemoveFailed'));
    }
  };

  const startEditingWp = (wp: WorkPackage) => {
    setEditingWpId(wp.id);
    setEditWp({
      title: wp.title || '',
      description: wp.description || '',
      scope: wp.scope || '',
      pricing_model: wp.pricing_model || 'fixed',
      price_amount: wp.price_amount != null ? String(wp.price_amount) : '',
      duration_days: wp.duration_days != null ? String(wp.duration_days) : '',
    });
  };

  const saveWpEdit = async () => {
    if (!proposal || !editingWpId) return;
    try {
      const updated = await proposalsApi.updateWorkPackage(editingWpId, {
        title: editWp.title,
        description: editWp.description,
        scope: editWp.scope,
        pricing_model: editWp.pricing_model,
        price_amount: editWp.price_amount ? parseFloat(editWp.price_amount) : null,
        duration_days: editWp.duration_days ? parseInt(editWp.duration_days) : null,
      });
      setProposal({
        ...proposal,
        work_packages: proposal.work_packages.map(wp => wp.id === editingWpId ? updated : wp),
      });
      setEditingWpId(null);
      toast.success(t('proposals.wpUpdated'));
    } catch {
      toast.error(t('proposals.wpUpdateFailed'));
    }
  };

  const generateAllSections = async () => {
    if (!proposal) return;
    try {
      setGenerating(true);
      setActiveTab('sections');
      const sections = await proposalsApi.generateAll(proposal.id);
      setProposal({ ...proposal, sections, status: 'review' });
      toast.success(t('proposals.allSectionsGenerated'));
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('proposals.generationFailed'));
    } finally {
      setGenerating(false);
    }
  };

  const regenerateSection = async (sectionType: string) => {
    if (!proposal) return;
    try {
      setGeneratingSection(sectionType);
      const section = await proposalsApi.generateSection(proposal.id, sectionType);
      setProposal({
        ...proposal,
        sections: proposal.sections.map(s =>
          s.section_type === sectionType ? section : s
        ),
      });
      toast.success(t('proposals.sectionRegenerated', { section: SECTION_LABELS[sectionType] || sectionType }));
    } catch {
      toast.error(t('proposals.regenerationFailed'));
    } finally {
      setGeneratingSection(null);
    }
  };

  const startEditing = (section: ProposalSection) => {
    setEditingSection(section.id);
    setEditContent(section.edited_content || section.generated_content || '');
  };

  const saveEdit = async (sectionId: number) => {
    try {
      const updated = await proposalsApi.updateSection(sectionId, { edited_content: editContent });
      if (proposal) {
        setProposal({
          ...proposal,
          sections: proposal.sections.map(s => s.id === sectionId ? updated : s),
        });
      }
      setEditingSection(null);
      toast.success(t('proposals.sectionUpdated'));
    } catch {
      toast.error(t('proposals.saveFailed'));
    }
  };

  const approveSection = async (sectionId: number) => {
    try {
      const updated = await proposalsApi.approveSection(sectionId);
      if (proposal) {
        setProposal({
          ...proposal,
          sections: proposal.sections.map(s => s.id === sectionId ? updated : s),
        });
      }
      toast.success(t('proposals.sectionApproved'));
    } catch {
      toast.error(t('proposals.approveFailed'));
    }
  };

  const createClient = async () => {
    if (!newClientName.trim()) return;
    try {
      const client = await clientsApi.create({
        name: newClientName, industry: newClientIndustry,
        contact_name: newClientContact, contact_email: newClientEmail,
      });
      setClients([...clients, client]);
      setSelectedClientId(client.id);
      setShowNewClient(false);
      setNewClientName(''); setNewClientIndustry('');
      setNewClientContact(''); setNewClientEmail('');
      toast.success(t('proposals.clientCreated'));
    } catch {
      toast.error(t('proposals.clientCreateFailed'));
    }
  };

  const exportProposal = async (format: 'pdf' | 'docx') => {
    if (!proposal) return;
    try {
      toast.info(t('proposals.generatingExport', { format: format.toUpperCase() }));
      const response = await api.get(`/proposals/${proposal.id}/export/${format}`, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'docx' ? 'docx' : 'pdf';
      link.setAttribute('download', `${title || 'proposal'}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('proposals.exportDownloaded', { format: format.toUpperCase() }));
    } catch {
      toast.error(t('proposals.exportFailed', { format: format.toUpperCase() }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const approvedCount = proposal?.sections?.filter(s => s.is_approved).length || 0;
  const totalSections = proposal?.sections?.length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" />
            {t('proposals.serviceProposal')}
          </h2>
          {proposal?.offer_number && (
            <p className="text-muted-foreground mt-1">{t('proposals.offerNumber', { number: proposal.offer_number })}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {proposal?.status && (
            <Badge variant={proposal.status === 'review' ? 'default' : 'secondary'}>
              {proposal.status}
            </Badge>
          )}
          {totalSections > 0 && (
            <Badge variant="outline">{approvedCount}/{totalSections} {t('proposals.approved')}</Badge>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="setup" className="flex items-center gap-1">
            <Building className="h-4 w-4" /> {t('proposals.clientInfo')}
          </TabsTrigger>
          <TabsTrigger value="packages" className="flex items-center gap-1" disabled={!proposal}>
            <Package className="h-4 w-4" /> {t('proposals.workPackages')}
          </TabsTrigger>
          <TabsTrigger value="sections" className="flex items-center gap-1" disabled={!proposal}>
            <Brain className="h-4 w-4" /> {t('proposals.aiSections')}
          </TabsTrigger>
        </TabsList>

        {/* SETUP TAB */}
        <TabsContent value="setup" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('proposals.proposalDetails')}</CardTitle>
              <CardDescription>{t('proposals.proposalDetailsDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('proposals.proposalTitle')} *</Label>
                  <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('proposals.proposalTitlePlaceholder')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('proposals.subtitle')}</Label>
                  <Input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder={t('proposals.subtitlePlaceholder')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('proposals.type')}</Label>
                  <Select value={proposalType} onValueChange={setProposalType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="angebot">{t('proposals.typeOffer')}</SelectItem>
                      <SelectItem value="vertrag">{t('proposals.typeContract')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('proposals.language')}</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="de">{t('proposals.languageGerman')}</SelectItem>
                      <SelectItem value="en">{t('proposals.languageEnglish')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Client Selection */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label>{t('proposals.client')}</Label>
                  <Button variant="ghost" size="sm" onClick={() => setShowNewClient(!showNewClient)}>
                    <Plus className="h-3 w-3 mr-1" /> {t('proposals.newClient')}
                  </Button>
                </div>
                <Select
                  value={selectedClientId?.toString() || ''}
                  onValueChange={v => setSelectedClientId(v ? parseInt(v) : null)}
                >
                  <SelectTrigger><SelectValue placeholder={t('proposals.selectClient')} /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.name} {c.industry && `(${c.industry})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {showNewClient && (
                  <div className="mt-3 p-3 border rounded-lg bg-muted/30 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Input value={newClientName} onChange={e => setNewClientName(e.target.value)} placeholder={`${t('proposals.clientName')} *`} />
                      <Input value={newClientIndustry} onChange={e => setNewClientIndustry(e.target.value)} placeholder={t('proposals.clientIndustry')} />
                      <Input value={newClientContact} onChange={e => setNewClientContact(e.target.value)} placeholder={t('proposals.contactPerson')} />
                      <Input value={newClientEmail} onChange={e => setNewClientEmail(e.target.value)} placeholder={t('proposals.email')} />
                    </div>
                    <Button size="sm" onClick={createClient} disabled={!newClientName.trim()}>
                      {t('proposals.createClient')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Contact Person */}
              {teamMembers.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('proposals.contactPersonLabel')}</Label>
                      <Select
                        value={contactMemberId?.toString() || ''}
                        onValueChange={v => setContactMemberId(v ? parseInt(v) : null)}
                      >
                        <SelectTrigger><SelectValue placeholder={t('proposals.selectTeamMember')} /></SelectTrigger>
                        <SelectContent>
                          {teamMembers.map(tm => (
                            <SelectItem key={tm.id} value={tm.id.toString()}>
                              {tm.title && `${tm.title} `}{tm.name} — {tm.role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('proposals.signatoryName')}</Label>
                      <Input value={signatoryName} onChange={e => setSignatoryName(e.target.value)} placeholder={t('proposals.signatoryPlaceholder')} />
                    </div>
                  </div>
                </div>
              )}

              {/* Problem Description */}
              <div className="border-t pt-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    {t('proposals.problemDescription')} *
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('proposals.problemDescriptionHint')}
                  </p>
                  <Textarea
                    value={problemDescription}
                    onChange={e => setProblemDescription(e.target.value)}
                    rows={8}
                    placeholder={t('proposals.problemPlaceholder')}
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              <Button
                onClick={createOrUpdateProposal}
                disabled={!title.trim() || !problemDescription.trim()}
                className="w-full"
              >
                {proposal ? t('proposals.updateProposal') : t('proposals.createProposal')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WORK PACKAGES TAB */}
        <TabsContent value="packages" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {t('proposals.workPackages')}
              </CardTitle>
              <CardDescription>
                {t('proposals.workPackagesDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing work packages */}
              {proposal?.work_packages?.map((wp, idx) => (
                <div key={wp.id} className="p-4 border rounded-lg bg-muted/20">
                  {editingWpId === wp.id ? (
                    <div className="space-y-3">
                      <Input value={editWp.title} onChange={e => setEditWp({ ...editWp, title: e.target.value })} placeholder={`${t('proposals.wpTitle')} *`} />
                      <Textarea value={editWp.description} onChange={e => setEditWp({ ...editWp, description: e.target.value })} rows={2} placeholder={t('proposals.wpDescription')} />
                      <Textarea value={editWp.scope} onChange={e => setEditWp({ ...editWp, scope: e.target.value })} rows={2} placeholder={t('proposals.wpScope')} />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Select value={editWp.pricing_model} onValueChange={v => setEditWp({ ...editWp, pricing_model: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">{t('proposals.pricingModels.fixed')}</SelectItem>
                            <SelectItem value="time_and_material">{t('proposals.pricingModels.time_and_material')}</SelectItem>
                            <SelectItem value="annual">{t('proposals.pricingModels.annual')}</SelectItem>
                            <SelectItem value="mixed">{t('proposals.pricingModels.mixed')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input type="number" value={editWp.price_amount} onChange={e => setEditWp({ ...editWp, price_amount: e.target.value })} placeholder={t('proposals.wpPrice')} />
                        <Input type="number" value={editWp.duration_days} onChange={e => setEditWp({ ...editWp, duration_days: e.target.value })} placeholder={t('proposals.wpDuration')} />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveWpEdit} disabled={!editWp.title.trim()}>
                          <Check className="h-4 w-4 mr-1" /> {t('common.save')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingWpId(null)}>{t('common.cancel')}</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1 cursor-pointer" onClick={() => startEditingWp(wp)}>
                        <div className="font-semibold">LS{idx + 1}: {wp.title}</div>
                        {wp.description && <p className="text-sm text-muted-foreground mt-1">{wp.description}</p>}
                        <div className="flex items-center gap-3 mt-2 text-sm">
                          <Badge variant="outline">{getPricingLabel(wp.pricing_model)}</Badge>
                          {wp.price_amount && (
                            <span className="font-medium">
                              {wp.price_amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })} {wp.price_currency}
                            </span>
                          )}
                          {wp.duration_days && (
                            <span className="text-muted-foreground">{wp.duration_days} days</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => startEditingWp(wp)} title={t('common.edit')}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => removeWorkPackage(wp.id)} title={t('common.delete')}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Predefined templates */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium mb-3">{t('proposals.quickAddTemplates')}</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  {t('proposals.quickAddTemplatesHint')}
                </p>
                {Array.from(new Set(WP_TEMPLATES.map(tpl => tpl.category))).map(category => (
                  <div key={category} className="mb-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">{category}</p>
                    <div className="flex flex-wrap gap-2">
                      {WP_TEMPLATES.filter(tpl => tpl.category === category).map(tpl => (
                        <Button
                          key={tpl.title}
                          variant="outline"
                          size="sm"
                          className="h-auto py-1.5 px-3 text-xs"
                          onClick={() => {
                            setWpTitle(tpl.title);
                            setWpDescription(tpl.description);
                            setWpScope(tpl.scope);
                            setWpPricingModel(tpl.pricing_model);
                            setWpPrice('');
                            setWpDuration('');
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1.5 shrink-0" />
                          {tpl.title}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Add new work package */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium mb-3">{t('proposals.addWorkPackage')}</h4>
                <div className="space-y-3">
                  <Input value={wpTitle} onChange={e => setWpTitle(e.target.value)} placeholder={`${t('proposals.wpTitle')} *`} />
                  <Textarea value={wpDescription} onChange={e => setWpDescription(e.target.value)} rows={2} placeholder={t('proposals.wpDescription')} />
                  <Textarea value={wpScope} onChange={e => setWpScope(e.target.value)} rows={2} placeholder={t('proposals.wpScope')} />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Select value={wpPricingModel} onValueChange={setWpPricingModel}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">{t('proposals.pricingModels.fixed')}</SelectItem>
                        <SelectItem value="time_and_material">{t('proposals.pricingModels.time_and_material')}</SelectItem>
                        <SelectItem value="annual">{t('proposals.pricingModels.annual')}</SelectItem>
                        <SelectItem value="mixed">{t('proposals.pricingModels.mixed')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" value={wpPrice} onChange={e => setWpPrice(e.target.value)} placeholder={t('proposals.wpPrice')} />
                    <Input type="number" value={wpDuration} onChange={e => setWpDuration(e.target.value)} placeholder={t('proposals.wpDuration')} />
                  </div>
                  <Button variant="outline" onClick={addWorkPackage} disabled={!wpTitle.trim()}>
                    <Plus className="h-4 w-4 mr-2" /> {t('proposals.addWorkPackage')}
                  </Button>
                </div>
              </div>

              {/* Generate button */}
              {proposal && proposal.work_packages.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <Button
                    onClick={generateAllSections}
                    disabled={generating || !problemDescription.trim()}
                    className="w-full"
                    size="lg"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        {t('proposals.generateAll')}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI SECTIONS TAB */}
        <TabsContent value="sections" className="space-y-4">
          {generating && (
            <Card className="border-primary/50 bg-primary/5">
              <CardContent className="py-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="mt-4 font-medium">{t('common.saving')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('proposals.generatingHint')}
                </p>
              </CardContent>
            </Card>
          )}

          {proposal?.sections?.sort((a, b) => a.order - b.order).map(section => (
            <Card key={section.id} className={section.is_approved ? 'border-green-500/30 bg-green-50/5' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {section.is_approved && <Check className="h-4 w-4 text-green-500" />}
                    {section.title}
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    {section.model_used && (
                      <Badge variant="outline" className="text-xs">{section.model_used}</Badge>
                    )}
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => regenerateSection(section.section_type)}
                      disabled={generatingSection === section.section_type}
                    >
                      {generatingSection === section.section_type ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => editingSection === section.id ? saveEdit(section.id) : startEditing(section)}
                      title={editingSection === section.id ? t('common.save') : t('common.edit')}
                    >
                      {editingSection === section.id ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Pencil className="h-4 w-4" />
                      )}
                    </Button>
                    {!section.is_approved && section.generated_content && (
                      <Button variant="ghost" size="icon" onClick={() => approveSection(section.id)} title={t('proposals.approveSection')}>
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {editingSection === section.id ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editContent}
                      onChange={e => setEditContent(e.target.value)}
                      rows={12}
                      className="font-mono text-sm"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit(section.id)}>
                        {t('common.save')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingSection(null)}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-ul:my-2 prose-li:my-0.5">
                    {(section.edited_content || section.generated_content) ? (
                      <ReactMarkdown>
                        {section.edited_content || section.generated_content || ''}
                      </ReactMarkdown>
                    ) : (
                      <p className="text-muted-foreground italic">
                        {t('proposals.notYetGenerated')}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {proposal && proposal.sections.length > 0 && !generating && (
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {approvedCount}/{totalSections} {t('proposals.approved')}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => exportProposal('docx')} variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" /> {t('common.exportWord')}
                    </Button>
                    <Button onClick={() => exportProposal('pdf')} variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" /> {t('common.exportPdf')}
                    </Button>
                    <Button onClick={generateAllSections} variant="outline" size="sm" disabled={generating}>
                      <RefreshCw className="h-4 w-4 mr-2" /> {t('proposals.regenerateAll')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
